/**
 * Builds Gap Analysis Questionnaire "Scope" fields from live project profile data
 * (attributes, profiling draft, scoping contract) so Review & Finalize is not dummy copy.
 */
import { resolveAdvisorDisplayName } from "./contractParties";

export function getProfileAttr(profile, key) {
  const attrs = profile?.attributes;
  if (!Array.isArray(attrs)) return "";
  const hit = attrs.find((a) => a.key === key);
  if (hit == null || hit.value === undefined || hit.value === null) return "";
  return String(hit.value).trim();
}

function parseLocationDetails(profile) {
  const raw = getProfileAttr(profile, "locationDetails");
  try {
    const arr = JSON.parse(raw || "[]");
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function formatLocationLine(profile) {
  const locs = parseLocationDetails(profile);
  if (!locs.length) {
    const hq = [getProfileAttr(profile, "hqCity"), getProfileAttr(profile, "hqCountry")].filter(Boolean).join(", ");
    return hq || "";
  }
  return locs
    .map((l) => {
      if (l?.city && l?.country) return `${l.city}, ${l.country}`;
      return l?.country || l?.city || "";
    })
    .filter(Boolean)
    .join("; ");
}

function pickApplicableSnippet(draft) {
  if (!draft || typeof draft !== "object") return "";
  const a = draft.applicable;
  if (typeof a === "string") return a.trim();
  if (Array.isArray(a) && a.length) {
    return a
      .map((row) => (typeof row === "object" && row?.title ? String(row.title) : String(row)))
      .filter(Boolean)
      .join("; ");
  }
  return "";
}

/** @param {Record<string, any> | null | undefined} project */
export function buildGapQuestionnaireScopeFromProject(project, firm = null) {
  const profile = project?.profile || {};
  const draft =
    profile.profilingDocumentDraft && typeof profile.profilingDocumentDraft === "object"
      ? profile.profilingDocumentDraft
      : {};
  const contract =
    profile.scopingContractDraft && typeof profile.scopingContractDraft === "object"
      ? profile.scopingContractDraft
      : {};

  const legalName = getProfileAttr(profile, "legalName") || "";
  const displayName = (project?.companyName || "").trim() || legalName || "";
  const locLine = formatLocationLine(profile);
  const brands = [getProfileAttr(profile, "companyBrands"), getProfileAttr(profile, "tradingName")]
    .filter(Boolean)
    .join("; ");

  let legalEntities = "";
  if (legalName && locLine) legalEntities = `${legalName}. Locations: ${locLine}.`;
  else if (legalName) legalEntities = legalName + (brands ? ` (${brands})` : "");
  else if (locLine) legalEntities = `Locations: ${locLine}`;
  else if (brands) legalEntities = brands;
  else legalEntities = displayName || "-";

  const productsParts = [
    getProfileAttr(profile, "productsAndServices"),
    getProfileAttr(profile, "linesOfBusiness"),
    getProfileAttr(profile, "aboutCompany"),
  ].filter(Boolean);
  const productsServices = productsParts.join(" ").trim() || pickApplicableSnippet(draft) || "-";

  const activitiesParts = [
    typeof draft.orgStructure === "string" && draft.orgStructure.trim(),
    getProfileAttr(profile, "gicsIndustry") && `GICS / industry: ${getProfileAttr(profile, "gicsIndustry")}`,
    getProfileAttr(profile, "companyHeadcount") && `Approx. headcount: ${getProfileAttr(profile, "companyHeadcount")}`,
  ].filter(Boolean);
  const activitiesDepts = activitiesParts.join("\n").trim() || "-";

  const otherBits = [
    typeof draft.execSummary === "string" && draft.execSummary.trim() && draft.execSummary.trim().slice(0, 1500),
    typeof draft.highLevel === "string" && draft.highLevel.trim() && `Scope notes: ${draft.highLevel.trim()}`.slice(0, 800),
    typeof contract.scopeOfWork === "string" && contract.scopeOfWork.trim() && contract.scopeOfWork.trim().slice(0, 1200),
  ].filter(Boolean);
  const otherInfo = otherBits.join("\n\n").trim();

  const contactName = getProfileAttr(profile, "contactName");
  const affirmationName = contactName
    ? `${contactName} (primary contact)`
    : displayName
      ? `Company SPOC - ${displayName}`
      : "Company SPOC";

  const advisor = resolveAdvisorDisplayName(contract, firm ?? project?.advisoryFirm);

  return {
    companyName: displayName || legalName || "-",
    advisorName: advisor,
    legalEntities,
    productsServices,
    activitiesDepts,
    otherInfo,
    affirmationName,
    affirmationDate: new Date().toISOString().split("T")[0],
  };
}
